import type { InboxPage, InboxSupportEntry } from '@/services/conversations/inbox';
import type { InquiryGroup } from '@/util/inquiryGroups';
import {
  getQuestionGroupRows,
  getUnreadSubmitterThreads,
  type QuestionGroup,
} from '@/util/questionGroups';

export type InboxRow =
  | { kind: 'inquiryGroup'; key: string; group: InquiryGroup }
  | { kind: 'questionGroup'; key: string; questionGroup: QuestionGroup }
  | { kind: 'supportThread'; key: string; entry: InboxSupportEntry };

/**
 * The rows of an inbox tab in the API's order.
 *
 * Inquiry grouping still happens in the app, so a group, which can gather
 * inquiries from anywhere in the list, takes the position of its earliest
 * member in the API's order. A support thread keeps its own position. A
 * conversation the API did not list, such as an inquiry a cache patch has just
 * prepended, sorts first, which is where the patch meant it to go.
 *
 * A staff inquiry group, which nests relay threads under their original, is placed the same way as a group:
 * at the position of its earliest member.
 *
 * Ties keep the groups' incoming order, so a tab with no support threads
 * renders exactly as it did before support threads existed.
 */
export function buildInboxRows(
  groups: readonly InquiryGroup[],
  pages: readonly (InboxPage | undefined)[],
  questionGroups: readonly QuestionGroup[] = []
): InboxRow[] {
  const positions = new Map<string, number>();

  pages.forEach((page) =>
    page?.order.forEach((conversationId) => {
      if (!positions.has(conversationId)) {
        positions.set(conversationId, positions.size);
      }
    })
  );

  const positionOf = (conversationId: string) => positions.get(conversationId) ?? -1;

  const groupRows = groups.map((group, order) => ({
    order,
    position: Math.min(...group.inquiries.map((inquiry) => positionOf(inquiry.conversationId))),
    row: { kind: 'inquiryGroup', key: group.groupKey, group } as InboxRow,
  }));

  const questionGroupRows = questionGroups.map((questionGroup, index) => ({
    order: groups.length + index,
    position: Math.min(
      ...(questionGroup.originalFetched ? [] : [positionOf(questionGroup.original.conversationId)]),
      ...getQuestionGroupRows(questionGroup).map((row) => positionOf(row.thread.conversationId)),
      ...getUnreadSubmitterThreads(questionGroup).map((thread) => positionOf(thread.conversationId))
    ),
    row: { kind: 'questionGroup', key: questionGroup.key, questionGroup } as InboxRow,
  }));

  const supportRows = pages
    .flatMap((page) => page?.supportThreads ?? [])
    .map((entry) => ({
      order: groups.length + questionGroups.length,
      position: positionOf(entry.conversationId),
      row: { kind: 'supportThread', key: `support-${entry.conversationId}`, entry } as InboxRow,
    }));

  return [...groupRows, ...questionGroupRows, ...supportRows]
    .sort((left, right) => left.position - right.position || left.order - right.order)
    .map(({ row }) => row);
}

/**
 * The key of the first inquiry group with several rows, which carries the one-time tip. An inquiry group with one
 * row has no caret for the tip to point at.
 */
export function getFirstMultiThreadQuestionGroupKey(rows: readonly InboxRow[]): string | undefined {
  return rows.find(
    (row) => row.kind === 'questionGroup' && getQuestionGroupRows(row.questionGroup).length > 1
  )?.key;
}
